import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import api from '../api';

const TX = '#111827';
const MU = '#6B7280';
const RD = '#EF4444';
const RL = '#FFF1F2';
const WH = '#FFFFFF';

const btn = (v) => ({
  padding: '9px 20px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: v === 'r' ? RL : v === 's' ? WH : '#111827',
  color: v === 'r' ? RD : v === 's' ? '#374151' : '#fff',
  boxShadow: v === 's'
    ? '0 1px 3px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.08)'
    : '0 1px 3px rgba(0,0,0,0.12)',
});

const CATEGORIES = [
  { key: 'companies', payloadKey: 'companyIds', title: 'Lead generated by — companies', hint: 'Account ownership' },
  { key: 'leads', payloadKey: 'leadIds', title: 'Lead bank assignments', hint: '' },
  { key: 'spocAssignments', payloadKey: 'spocIds', title: 'Daily queue assignments', hint: '' },
  { key: 'meetings', payloadKey: 'meetingIds', title: 'Meetings conducted by', hint: 'Leave unchecked to keep historical credit' },
  { key: 'calls', payloadKey: 'callIds', title: 'Calls logged by', hint: '' },
  { key: 'proposals', payloadKey: 'proposalIds', title: 'Proposals', hint: '' },
];

function buildInitialSelected(preview) {
  const items = preview?.items || {};
  return {
    companies: (items.companies || []).map(i => i.id),
    leads: (items.leads || []).map(i => i.id),
    spocAssignments: (items.spocAssignments || []).map(i => i.id),
    meetings: [],
    calls: [],
    proposals: [],
  };
}

function buildSelections(selected) {
  return {
    companyIds: selected.companies,
    leadIds: selected.leads,
    spocIds: selected.spocAssignments,
    meetingIds: selected.meetings,
    callIds: selected.calls,
    proposalIds: selected.proposals,
  };
}

function countSelected(selected) {
  return Object.values(selected).reduce((n, arr) => n + arr.length, 0);
}

function TransferCategory({ title, hint, items, selectedIds, onChangeSelected }) {
  const selectAllRef = useRef(null);
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleOne = (id) => {
    onChangeSelected(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        background: '#F9FAFB',
        borderBottom: items.length ? '1px solid #E5E7EB' : 'none',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TX }}>{title}</div>
          {hint ? <div style={{ fontSize: 11, color: MU, marginTop: 2 }}>{hint}</div> : null}
          {items.length > 0 ? (
            <div style={{ fontSize: 11, color: MU, marginTop: 2 }}>
              {selectedIds.length} of {items.length} selected
            </div>
          ) : null}
        </div>
        {items.length > 0 ? (
          <label style={{ fontSize: 11, fontWeight: 600, color: MU, cursor: 'pointer', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allSelected}
              onChange={() => onChangeSelected(allSelected ? [] : items.map(i => i.id))}
            />
            {allSelected ? 'Deselect all' : 'Select all'}
          </label>
        ) : null}
      </div>
      {items.length === 0 ? (
        <div style={{ padding: '10px 12px', fontSize: 12, color: MU }}>None linked to this user</div>
      ) : (
        <div style={{ maxHeight: 150, overflowY: 'auto' }}>
          {items.map((item, idx) => (
            <label
              key={item.id}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                padding: '8px 12px',
                fontSize: 12,
                cursor: 'pointer',
                borderBottom: idx < items.length - 1 ? '1px solid #F3F4F6' : 'none',
              }}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleOne(item.id)}
                style={{ marginTop: 2 }}
              />
              <span style={{ color: TX, lineHeight: 1.45 }}>{item.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RemoveUserModal({ open, user, preview, recipients, onClose, onRemoved }) {
  const [step, setStep] = useState('select');
  const [transferTo, setTransferTo] = useState('');
  const [selected, setSelected] = useState(() => buildInitialSelected(preview));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !preview) return;
    setStep('select');
    setSelected(buildInitialSelected(preview));
    setTransferTo(recipients[0] || '');
    setLoading(false);
  }, [open, preview, user, recipients]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev) => { if (ev.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, loading, onClose]);

  const totalSelected = useMemo(() => countSelected(selected), [selected]);
  const needsRecipient = totalSelected > 0;

  const summaryLines = useMemo(() => {
    if (!preview?.items) return [];
    return CATEGORIES
      .map(cat => ({ ...cat, count: selected[cat.key].length }))
      .filter(cat => cat.count > 0);
  }, [preview, selected]);

  if (!open || !user || !preview) return null;

  const canContinue = !needsRecipient || (recipients.length > 0 && transferTo);
  const canConfirm = !needsRecipient || transferTo;

  const handleRemove = async () => {
    setLoading(true);
    try {
      const selections = buildSelections(selected);
      const transferAccounts = selections.companyIds.length + selections.leadIds.length + selections.spocIds.length > 0;
      const transferActivity = selections.meetingIds.length + selections.callIds.length + selections.proposalIds.length > 0;
      const body = {
        transferTo: transferTo || undefined,
        transferAccounts,
        transferActivity,
      };
      if (totalSelected > 0) body.selections = selections;
      await api.deleteUser(String(user._id || user.id), body);
      onRemoved?.();
    } catch (e) {
      alert(e.message || 'Could not remove user');
      setLoading(false);
    }
  };

  const recordCounts = [
    { label: 'Companies', value: preview.companies || 0 },
    { label: 'Lead bank', value: preview.leads || 0 },
    { label: 'Queue', value: preview.spocAssignments || 0 },
    { label: 'Meetings conducted', value: preview.meetingsConducted || 0 },
    { label: 'Calls', value: preview.calls || 0 },
    { label: 'Proposals', value: preview.proposals || 0 },
  ];

  return (
    <div className="app-modal-overlay app-modal-overlay--large" style={{ zIndex: 110 }} onClick={loading ? undefined : onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
        className="app-modal-panel app-modal-panel--wide"
        style={{ padding: '28px 32px', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
            {step === 'confirm' ? 'Confirm removal' : 'Remove team member'}
          </h2>
          <div
            onClick={loading ? undefined : onClose}
            style={{ cursor: loading ? 'default' : 'pointer', color: MU, fontSize: 16, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: '#F5F5F7' }}
          >
            ✕
          </div>
        </div>

        {step === 'select' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, color: MU, lineHeight: 1.6 }}>
              Remove <strong style={{ color: TX }}>{user.username}</strong> from the team.
              Choose which records to transfer, or leave categories unchecked to keep them unchanged.
              Meeting and call credit stays with {user.username} unless you select those items.
            </div>

            <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: TX, marginBottom: 10 }}>Records linked to {user.username}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, fontSize: 12 }}>
                {recordCounts.map(row => (
                  <div key={row.label}><strong>{row.value}</strong> {row.label.toLowerCase()}</div>
                ))}
              </div>
            </div>

            {recipients.length > 0 ? (
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: MU, display: 'block', marginBottom: 6 }}>
                  Transfer selected records to
                </label>
                <select
                  value={transferTo}
                  onChange={e => setTransferTo(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #E5E7EB',
                    fontSize: 13,
                    width: '100%',
                    boxSizing: 'border-box',
                    background: WH,
                    color: TX,
                  }}
                >
                  <option value="">Select teammate…</option>
                  {recipients.map(sp => <option key={sp} value={sp}>{sp}</option>)}
                </select>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: RD, background: RL, border: '1px solid #FECACA', borderRadius: 10, padding: '10px 12px' }}>
                Add another team member before removing {user.username} if any records need to be transferred.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {CATEGORIES.map(cat => (
                <TransferCategory
                  key={cat.key}
                  title={cat.title}
                  hint={cat.hint}
                  items={preview.items?.[cat.key] || []}
                  selectedIds={selected[cat.key]}
                  onChangeSelected={ids => setSelected(prev => ({ ...prev, [cat.key]: ids }))}
                />
              ))}
            </div>

            {totalSelected === 0 && (
              <div style={{ fontSize: 12, color: MU, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 12px' }}>
                No records selected for transfer. {user.username} will be removed and their linked records will stay as-is (you may need to reassign them manually later).
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={btn('s')} disabled={loading} onClick={onClose}>
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                style={{ ...btn('g'), opacity: canContinue ? 1 : 0.5 }}
                disabled={loading || !canContinue}
                onClick={() => setStep('confirm')}
              >
                Review & confirm
              </motion.button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, color: MU, lineHeight: 1.6 }}>
              You are about to remove <strong style={{ color: TX }}>{user.username}</strong>
              {totalSelected > 0 && transferTo ? (
                <> and transfer <strong style={{ color: TX }}>{totalSelected}</strong> record{totalSelected === 1 ? '' : 's'} to <strong style={{ color: TX }}>{transferTo}</strong>.</>
              ) : (
                <> with no records transferred.</>
              )}
            </div>

            {summaryLines.length > 0 && (
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: TX, marginBottom: 10 }}>Transfer summary</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                  {summaryLines.map(line => (
                    <div key={line.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: MU }}>{line.title}</span>
                      <strong style={{ color: TX }}>{line.count}</strong>
                    </div>
                  ))}
                  {transferTo ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 6, paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                      <span style={{ color: MU }}>Receiver</span>
                      <strong style={{ color: TX }}>{transferTo}</strong>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, color: RD, background: RL, border: '1px solid #FECACA', borderRadius: 10, padding: '10px 12px' }}>
              This cannot be undone. The user account will be deleted immediately.
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={btn('s')} disabled={loading} onClick={() => setStep('select')}>
                Back
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                style={{ ...btn('r'), opacity: loading ? 0.6 : 1 }}
                disabled={loading || !canConfirm}
                onClick={handleRemove}
              >
                {loading ? 'Removing…' : 'Remove user'}
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
